import { useRef, useState, type ReactNode, type TouchEvent } from 'react';
import {
  cleDuJour,
  dateComplete,
  grilleDuMois,
  grouperJoursConsecutifs,
  jourEtMois,
  moisEtAnnee,
  nomsJoursCourts,
} from '@repere/core';
import { Bouton, Invisible } from './index';
import { useTraduction } from '../i18n/langue';
import { maintenant } from '../horloge';
import type { FenetreAnnees } from './ChampDateNaissance';

/**
 * Choix de jours sur un calendrier — un jour, ou plusieurs, cochés un à un.
 *
 * Il remplace les deux listes « Du » et « Au » de la demande d'échange, et le changement
 * n'est pas seulement cosmétique : six listes déroulantes demandaient de connaître la date
 * du samedi qu'on veut échanger, alors que la question se pose sur une grille — « ce
 * week-end-là », « le mercredi et le jeudi du pont ». La grille montre les jours de la
 * semaine, donc elle répond à la question telle qu'elle se pose.
 *
 * **Les jours cochés n'ont pas à se suivre.** Deux jours séparés forment deux périodes
 * (`grouperJoursConsecutifs`), jamais une seule qui emporterait sans le dire tout ce qu'il
 * y a entre les deux. C'est exactement ce que « Du … au … » ne savait pas exprimer : il
 * fallait déposer deux demandes pour deux samedis.
 *
 * Ce n'est **pas** `ChampDate` avec une autre peau : ce champ-là sert les dates lointaines
 * et exactes — une naissance en 2017 — où une grille oblige à reculer de mois en mois.
 * Ici la fenêtre tient en quelques semaines, et c'est le calendrier qui est le bon outil.
 * Les deux coexistent pour cette raison.
 *
 * Accessibilité : chaque case est un `<button aria-pressed>` portant la date complète en
 * texte invisible. Un tableau de `div` cliquables ne serait ni tabulable, ni annoncé, ni
 * actionnable au clavier — et l'état « choisi » ne tient pas à la seule couleur.
 */

/** Bornes de navigation : on ne feuillette pas vingt-cinq ans pour un échange de jours. */
const FENETRE_PAR_DEFAUT: FenetreAnnees = { avant: 1, apres: 1 };

/** Distance minimale, en pixels, avant qu'un geste horizontal ne compte comme un balayage. */
const SEUIL_BALAYAGE = 40;

/** Le premier du mois, seule forme sous laquelle le curseur de mois existe. */
const premierDuMois = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

/** Distance en mois, signée — négative si `mois` précède `reference`. */
const ecartEnMois = (mois: Date, reference: Date): number =>
  (mois.getFullYear() - reference.getFullYear()) * 12 + (mois.getMonth() - reference.getMonth());

/**
 * Une série de jours consécutifs, dite en toutes lettres.
 *
 * « le 7 octobre » pour un jour seul, « du 7 au 9 octobre » pour une suite : le récapitulatif
 * doit se relire comme la demande qui partira, pas comme une liste de dates.
 */
function serieEnMots(serie: string[], locale: Parameters<typeof jourEtMois>[1]): string {
  const premier = new Date(`${serie[0]}T00:00:00`);
  const dernier = new Date(`${serie[serie.length - 1]}T00:00:00`);
  if (serie.length === 1) return `le ${jourEtMois(premier, locale)}`;
  return `du ${jourEtMois(premier, locale)} au ${jourEtMois(dernier, locale)}`;
}

export function ChampJours({
  label,
  valeur,
  onChange,
  aide,
  fenetre = FENETRE_PAR_DEFAUT,
  moisInitial,
}: {
  label: ReactNode;
  /** Les jours choisis, en clés `AAAA-MM-JJ`. Ni ordre ni continuité garantis en entrée. */
  valeur: readonly string[];
  onChange: (jours: string[]) => void;
  aide?: string;
  fenetre?: FenetreAnnees;
  /** Mois affiché à l'ouverture. Le mois courant par défaut. */
  moisInitial?: Date;
}) {
  const { locale } = useTraduction();
  const [curseur, setCurseur] = useState(() => premierDuMois(moisInitial ?? maintenant()));

  const choisis = new Set(valeur);

  // Les bornes se comptent en mois pleins autour du mois courant : une fenêtre d'un an de
  // part et d'autre s'arrête au même mois de l'année précédente et de la suivante, et non
  // au 1er janvier, qui couperait la moitié de la fenêtre attendue.
  const moisCourant = premierDuMois(maintenant());
  const ecart = ecartEnMois(curseur, moisCourant);
  const peutReculer = ecart > -12 * fenetre.avant;
  const peutAvancer = ecart < 12 * fenetre.apres;

  const changerDeMois = (delta: number) => {
    if (delta < 0 ? !peutReculer : !peutAvancer) return;
    setCurseur((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };

  const basculer = (jour: Date) => {
    const cle = cleDuJour(jour);
    const suivants = choisis.has(cle)
      ? valeur.filter((j) => j !== cle)
      : [...valeur, cle].sort();
    onChange([...suivants]);
  };

  /**
   * Le balayage change de mois, comme sur le calendrier de garde : c'est le geste attendu
   * sur une grille de dates, et il double les deux boutons plutôt que de les remplacer —
   * un geste sans commande visible n'est annoncé ni au clavier, ni au lecteur d'écran.
   */
  const balayage = useRef<{ x: number; y: number } | null>(null);
  const surDebutBalayage = (e: TouchEvent) => {
    const point = e.touches[0];
    balayage.current = point ? { x: point.clientX, y: point.clientY } : null;
  };
  const surFinBalayage = (e: TouchEvent) => {
    const depart = balayage.current;
    balayage.current = null;
    const point = e.changedTouches[0];
    if (!depart || !point) return;
    const dx = point.clientX - depart.x;
    const dy = point.clientY - depart.y;
    if (Math.abs(dx) < SEUIL_BALAYAGE || Math.abs(dx) < Math.abs(dy)) return;
    changerDeMois(dx < 0 ? 1 : -1);
  };

  const semaines = grilleDuMois(curseur.getFullYear(), curseur.getMonth());
  const joursCourts = nomsJoursCourts(locale);
  const series = grouperJoursConsecutifs(valeur);

  return (
    <fieldset className="champ champ-jours">
      <legend>{label}</legend>

      <div className="calendrier-mois">
        <Bouton variante="secondaire" onClick={() => changerDeMois(-1)} disabled={!peutReculer}>
          Mois précédent
        </Bouton>
        <strong aria-live="polite">{moisEtAnnee(curseur, locale)}</strong>
        <Bouton variante="secondaire" onClick={() => changerDeMois(1)} disabled={!peutAvancer}>
          Mois suivant
        </Bouton>
      </div>

      <table
        className="calendrier calendrier-choix"
        onTouchStart={surDebutBalayage}
        onTouchEnd={surFinBalayage}
      >
        <caption>
          <Invisible>Choix des jours — {moisEtAnnee(curseur, locale)}</Invisible>
        </caption>
        <thead>
          <tr>
            {joursCourts.map((jour) => (
              <th key={jour} scope="col">
                {jour}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semaines.map((semaine, index) => (
            <tr key={index}>
              {semaine.map((jour, position) => {
                if (!jour) return <td key={position} className="hors-mois" />;
                const cle = cleDuJour(jour);
                const choisi = choisis.has(cle);
                return (
                  <td key={position} className={choisi ? 'jour-choisi' : undefined}>
                    <button
                      type="button"
                      className="case-jour"
                      aria-pressed={choisi}
                      onClick={() => basculer(jour)}
                    >
                      <span aria-hidden="true">{jour.getDate()}</span>
                      <Invisible>{dateComplete(jour, locale)}</Invisible>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Le récapitulatif dit la demande telle qu'elle partira — en périodes, pas en cases
          cochées. Un parent qui a coché deux samedis doit lire deux périodes, sinon il
          croira proposer la semaine entre les deux. `role="status"` : la sélection change
          sans que rien ne bouge à l'écran hors de la grille. */}
      <p className="champ-jours-recap" role="status">
        {series.length === 0 ? (
          <span className="discret">Aucun jour choisi.</span>
        ) : (
          <>
            {valeur.length === 1 ? '1 jour choisi' : `${valeur.length} jours choisis`} :{' '}
            {series.map((serie) => serieEnMots(serie, locale)).join(', ')}.{' '}
            <Bouton variante="lien" onClick={() => onChange([])}>
              Tout effacer
            </Bouton>
          </>
        )}
      </p>

      {aide ? <p className="aide">{aide}</p> : null}
    </fieldset>
  );
}
